import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

import {
  actionColumnWidth,
  ColumnData,
  Filters,
  FilterValue,
  isRangeFilter,
  RangeFilter,
  RowBase,
  selectionColumnWidth,
  Sorting,
} from './priceGridTypes';

interface Props<TRow extends RowBase> {
  readonly columns: readonly ColumnData<TRow>[];
  readonly sortings: readonly Sorting[];
  readonly onToggleSort: (columnKey: string) => void;
  readonly onSetSort: (columnKey: string, direction: Sorting['direction']) => void;
  readonly filters: Filters;
  readonly filterOptionsByColumnKey: { readonly [columnKey: string]: readonly string[] };
  readonly onFilterChange: (columnKey: string, filterValue: FilterValue) => void;
  readonly groupByColumnKey: string;
  readonly onSetGroupBy: (columnKey: string) => void;
  readonly onClearColumn: (columnKey: string) => void;
  readonly hasEditPermission: boolean;
  readonly allFilteredRowsSelected: boolean;
  readonly someFilteredRowsSelected: boolean;
  readonly filteredRowCount: number;
  readonly onSetAllFilteredRowsSelected: (selected: boolean) => void;
}

interface ColumnMenuState {
  readonly anchorEl: HTMLElement;
  readonly columnKey: string;
}

export function PriceGridHeader<TRow extends RowBase>({
  columns,
  sortings,
  onToggleSort,
  onSetSort,
  filters,
  filterOptionsByColumnKey,
  onFilterChange,
  groupByColumnKey,
  onSetGroupBy,
  onClearColumn,
  hasEditPermission,
  allFilteredRowsSelected,
  someFilteredRowsSelected,
  filteredRowCount,
  onSetAllFilteredRowsSelected,
}: Props<TRow>) {
  const [columnMenu, setColumnMenu] = useState<ColumnMenuState | null>(null);
  const [expandedTextFilterKey, setExpandedTextFilterKey] = useState('');

  const getTextFilterValue = (columnKey: string) => {
    const filterValue = filters[columnKey];
    return typeof filterValue === 'string' ? filterValue : '';
  };

  const getSelectFilterValues = (columnKey: string) => {
    const filterValue = filters[columnKey];
    return Array.isArray(filterValue) ? [...filterValue] : [];
  };

  const getRangeFilterValue = (columnKey: string): RangeFilter => {
    const filterValue = filters[columnKey];
    return isRangeFilter(filterValue) ? filterValue : {};
  };

  const activeMenuColumn = columns.find((column) => column.key === columnMenu?.columnKey);

  const runMenuAction = (action: () => void) => {
    action();
    setColumnMenu(null);
  };

  return (
    <>
      <TableRow>
        <TableCell variant="head" sx={{ padding: 0, textAlign: 'center', width: selectionColumnWidth }}>
          <Checkbox
            checked={allFilteredRowsSelected}
            disabled={!hasEditPermission || filteredRowCount === 0}
            indeterminate={someFilteredRowsSelected && !allFilteredRowsSelected}
            size="small"
            title="Select all filtered rows"
            onChange={(event) => onSetAllFilteredRowsSelected(event.target.checked)}
          />
        </TableCell>
        {columns.map((column) => {
          const sorting = sortings.find((candidate) => candidate.columnKey === column.key);
          const isGrouped = groupByColumnKey === column.key;

          return (
            <TableCell key={column.key} variant="head" sx={{ width: column.width, paddingX: 1, paddingY: 0.5 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
                <TableSortLabel
                  active={Boolean(sorting)}
                  direction={sorting?.direction}
                  hideSortIcon
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    '& .MuiTableSortLabel-icon': { flex: '0 0 auto' },
                  }}
                  onClick={() => onToggleSort(column.key)}
                >
                  <Typography component="span" noWrap variant="subtitle2">
                    {column.title}
                  </Typography>
                </TableSortLabel>
                {isGrouped && (
                  <Tooltip title={`Grouped by ${column.title}`}>
                    <ViewListIcon color="primary" fontSize="small" sx={{ flex: '0 0 auto' }} />
                  </Tooltip>
                )}
                <Tooltip title={`${column.title} options`}>
                  <IconButton
                    aria-label={`${column.title} options`}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      flex: '0 0 auto',
                      height: 24,
                      opacity: 0.56,
                      p: 0.25,
                      width: 24,
                      '&:hover, &:focus-visible': { opacity: 1 },
                    }}
                    onClick={(event) => setColumnMenu({ anchorEl: event.currentTarget, columnKey: column.key })}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          );
        })}
        <TableCell sx={{ width: actionColumnWidth }} />
      </TableRow>
      <TableRow>
        <TableCell sx={{ padding: 0, width: selectionColumnWidth }} />
        {columns.map((column) => {
          const rangeFilter = getRangeFilterValue(column.key);
          const textFilter = getTextFilterValue(column.key);
          const isTextFilterExpanded = expandedTextFilterKey === column.key || Boolean(textFilter);

          return (
            <TableCell key={column.key} sx={{ paddingX: 1, paddingY: 0.5 }}>
              {column.filterType === 'select' && (
                <Select<string[]>
                  displayEmpty
                  fullWidth
                  multiple
                  size="small"
                  value={getSelectFilterValues(column.key)}
                  onChange={(event: SelectChangeEvent<string[]>) => {
                    const value = event.target.value;
                    const selectedValues = typeof value === 'string' ? value.split(',') : value;

                    onFilterChange(column.key, selectedValues.length === 0 ? undefined : selectedValues);
                  }}
                  renderValue={(selectedValues) =>
                    selectedValues.length === 0
                      ? (column.filterLabel ?? `All ${column.title.toLowerCase()}`)
                      : selectedValues.join(', ')
                  }
                  sx={{ fontSize: '13px', '& .MuiSelect-select': { py: 0.5 } }}
                >
                  {(filterOptionsByColumnKey[column.key] ?? []).map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={getSelectFilterValues(column.key).includes(option)} size="small" />
                      <Typography noWrap variant="body2">
                        {option}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}
              {column.filterType === 'range' && (
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                  <Input
                    fullWidth
                    inputProps={{ 'aria-label': column.rangeFilterLabels?.min ?? `Minimum ${column.title}` }}
                    placeholder={column.rangeFilterLabels?.min ?? 'Min'}
                    type="number"
                    value={rangeFilter.min ?? ''}
                    onChange={(event) =>
                      onFilterChange(column.key, { ...rangeFilter, min: event.target.value || undefined })
                    }
                    sx={{ fontSize: '13px' }}
                  />
                  <Input
                    fullWidth
                    inputProps={{ 'aria-label': column.rangeFilterLabels?.max ?? `Maximum ${column.title}` }}
                    placeholder={column.rangeFilterLabels?.max ?? 'Max'}
                    type="number"
                    value={rangeFilter.max ?? ''}
                    onChange={(event) =>
                      onFilterChange(column.key, { ...rangeFilter, max: event.target.value || undefined })
                    }
                    sx={{ fontSize: '13px' }}
                  />
                </Box>
              )}
              {column.filterType === 'text' &&
                (isTextFilterExpanded ? (
                  <Input
                    autoFocus={expandedTextFilterKey === column.key}
                    fullWidth
                    inputProps={{ 'aria-label': column.filterLabel ?? `Search ${column.title}` }}
                    placeholder={column.filterLabel ?? `Search ${column.title.toLowerCase()}`}
                    startAdornment={<SearchIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />}
                    value={textFilter}
                    onBlur={() => {
                      if (!textFilter) setExpandedTextFilterKey('');
                    }}
                    onChange={(event) => onFilterChange(column.key, event.target.value || undefined)}
                    sx={{ fontSize: '13px' }}
                  />
                ) : (
                  <Tooltip title={column.filterLabel ?? `Search ${column.title}`}>
                    <IconButton
                      aria-label={column.filterLabel ?? `Search ${column.title}`}
                      size="small"
                      onClick={() => setExpandedTextFilterKey(column.key)}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ))}
            </TableCell>
          );
        })}
        <TableCell />
      </TableRow>
      <Menu
        anchorEl={columnMenu?.anchorEl}
        open={Boolean(columnMenu)}
        onClose={() => setColumnMenu(null)}
        slotProps={{ list: { 'aria-label': activeMenuColumn ? `${activeMenuColumn.title} options` : undefined } }}
      >
        {activeMenuColumn && [
          <MenuItem
            key="sort-asc"
            selected={sortings.some(
              (sorting) => sorting.columnKey === activeMenuColumn.key && sorting.direction === 'asc',
            )}
            onClick={() => runMenuAction(() => onSetSort(activeMenuColumn.key, 'asc'))}
          >
            Sort ascending
          </MenuItem>,
          <MenuItem
            key="sort-desc"
            selected={sortings.some(
              (sorting) => sorting.columnKey === activeMenuColumn.key && sorting.direction === 'desc',
            )}
            onClick={() => runMenuAction(() => onSetSort(activeMenuColumn.key, 'desc'))}
          >
            Sort descending
          </MenuItem>,
          activeMenuColumn.groupable ? (
            <MenuItem
              key="group"
              selected={groupByColumnKey === activeMenuColumn.key}
              onClick={() => runMenuAction(() => onSetGroupBy(activeMenuColumn.key))}
            >
              Group by this column
            </MenuItem>
          ) : null,
          <Divider key="divider" />,
          <MenuItem key="clear" onClick={() => runMenuAction(() => onClearColumn(activeMenuColumn.key))}>
            Clear
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
}
